import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AuthService } from './auth.service';

export interface LogoutOptions {
  confirmationMessage?: string;
  skipConfirmation?: boolean;
  reason?: string;
  silent?: boolean;
  redirectUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  private defaultConfirmationMessage = 'Are you sure you want to logout?';

  constructor(private authService: AuthService) {}

  /**
   * Logout with custom options
   */
  logout(options: LogoutOptions = {}): Observable<boolean> {
    const {
      confirmationMessage = this.defaultConfirmationMessage,
      skipConfirmation = false,
      reason,
      silent = false,
      redirectUrl
    } = options;

    // If confirmation is needed and not skipped
    if (!skipConfirmation && !silent) {
      return new Observable(observer => {
        const confirmed = window.confirm(confirmationMessage);
        if (confirmed) {
          this.performLogout(silent, reason, redirectUrl).subscribe({
            next: (success) => {
              observer.next(success);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            }
          });
        } else {
          observer.next(false);
          observer.complete();
        }
      });
    } else {
      // Direct logout without confirmation
      return this.performLogout(silent, reason, redirectUrl);
    }
  }

  /**
   * Force logout for security scenarios
   */
  forceLogout(reason: string = 'Security logout'): void {
    this.authService.forceLogout(reason);
  }

  /**
   * Logout due to session expiry
   */
  sessionExpiredLogout(): void {
    this.forceLogout('Session expired');
  }

  /**
   * Logout due to inactivity
   */
  inactivityLogout(): void {
    this.forceLogout('Logged out due to inactivity');
  }

  /**
   * Logout all sessions (if supported by backend)
   */
  logoutAllSessions(): Observable<boolean> {
    return this.logout({
      confirmationMessage: 'This will logout from all devices. Are you sure?',
      reason: 'Logout from all sessions'
    });
  }

  private performLogout(silent: boolean = false, reason?: string, redirectUrl?: string): Observable<boolean> {
    if (reason) {
      console.log('Logout reason:', reason);
    }

    return this.authService.logout(silent);
  }
} 